import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { usePageContext } from "../../context/PageContext";
import { useClientGroups } from "../../hooks/useClientGroups";
import AwarenessStageTable from "../../components/AwarenessStageTable";
import AddAwarenessStageForm from "../../components/AddAwarenessStageForm";
import EmptyStateAwarenessLevels from "../../components/EmptyStateAwarenessLevels";
import { fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";

const AwarenessStages = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingSugestao, setLoadingSugestao] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useClientGroups(currentPage, pageSize, search);

  const groups = data?.groups || [];
  const isEmpty = !isLoading && groups.length === 0;

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar etapas. Tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  // ações linha
  const handleEdit = (row) => { setSelected(row); setErrorMessage(null); };
  const handleAddChild = (parent) => { setSelected({ id_pai: parent?.id }); setErrorMessage(null); };
  const handleDelete = (id) => { setConfirmDelete(id); setErrorMessage(null); };

  const confirmDeleteRow = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => { setConfirmDelete(null); refetch(); },
      onError: (err) => setErrorMessage(err?.response?.data?.message || "Erro ao excluir a etapa."),
      onSettled: () => setIsProcessing(false),
    });
  };

  const toggleStatus = (row) => {
    // Ativo = excluido 0; Inativo = excluido 1
    setIsProcessing(true);
    updateMutation.mutate(
      { id: row.id, excluido: row.excluido ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao alterar status."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  // ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, excluido: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao ativar etapas.");
    } finally { setIsProcessing(false); }
  };
  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, excluido: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao desativar etapas.");
    } finally { setIsProcessing(false); }
  };
  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao excluir etapas.");
    } finally { setIsProcessing(false); }
  };

  // paginação/busca
  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => { setSearch(term); refetch(); };
  const handleCloseForm = () => { setSelected(null); setErrorMessage(null); };

  const handleCriarComSugestoesIA = async () => {
    setLoadingSugestao(true);
    setErrorMessage(null);
    try {
      const response = await fetchData("/setup", "POST", {
        etapaAtual: 2,
        dadosEtapa: {},
      });
      // Suporte a response.data ou response.data.data (formato da API)
      const raw = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      const seen = new Set();
      const suggested = raw.filter((item) => {
        const nome = (item?.title || item?.nome || "").trim();
        const key = nome.toLowerCase();
        if (!nome || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (suggested.length === 0) {
        setErrorMessage("Nenhuma sugestão retornada. Tente novamente.");
        return;
      }
      // Um único projeto para não criar um registro por empresa (evita triplicar)
      let idsProjetos = null;
      try {
        const stored = sessionStorage.getItem("selectedCompanies");
        if (stored && stored !== "undefined") {
          const arr = JSON.parse(stored);
          if (Array.isArray(arr) && arr.length > 0) idsProjetos = [arr[0]];
        }
      } catch (_) {}
      for (let i = 0; i < suggested.length; i++) {
        const item = suggested[i];
        const nome = item?.title || item?.nome || `Nível ${i + 1}`;
        const payload = { nome, ordem: i };
        if (idsProjetos) payload.ids_projetos = idsProjetos;
        await updateMutation.mutateAsync(payload);
      }
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao criar níveis com IA.");
    } finally {
      setLoadingSugestao(false);
    }
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex", flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center",
          mb: 2, gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("awarenessPage.heading")}</Typography>
        <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => setSelected({})}>
          {t("awarenessPage.addStage")}
        </Button>
      </Box>

      {errorMessage && <Alert sx={{ mb: 2 }} severity="error">{errorMessage}</Alert>}

      {selected ? (
        <AddAwarenessStageForm
          groups={groups} 
          data={selected}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            setIsProcessing(true);
            updateMutation.mutate(payload, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) => setErrorMessage(err?.response?.data?.message || "Erro ao salvar."),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : isEmpty ? (
        <EmptyStateAwarenessLevels
          onPrimaryClick={handleCriarComSugestoesIA}
          onSecondaryClick={() => setSelected({})}
          primaryLoading={loadingSugestao}
        />
      ) : (
        <AwarenessStageTable
          groups={groups}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onAddChild={handleAddChild}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleStatus={toggleStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("shared.deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("shared.processingDelete") : t("awarenessPage.deleteQuestion")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteRow}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AwarenessStages;
